import React, { useEffect, useState } from "react"
import { BiInfoCircle } from "react-icons/bi"
import { HiOutlineGlobeAlt } from "react-icons/hi"
import ReactMarkdown from "react-markdown"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import ConfirmationModal from "../components/common/ConfirmationModal"
import Footer from "../components/common/Footer"
import RatingStars from "../components/common/RatingStars"
import CourseAccordionBar from "../components/core/Course/CourseAccordionBar"
import CourseDetailsCard from "../components/core/Course/CourseDetailsCard"
import { formatDate } from "../services/formatDate"
import { fetchCourseDetails } from "../services/operations/courseDetailsAPI"
import { buyCourse } from "../services/operations/studentFeaturesAPI"
import GetAvgRating from "../utils/avgRating"
import Error from "./Error"

function CourseDetails() {
  const { user } = useSelector((state) => state.profile)
  const { token } = useSelector((state) => state.auth)
  const { loading } = useSelector((state) => state.profile)
  const { paymentLoading } = useSelector((state) => state.course)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { courseId } = useParams()

  const [response, setResponse] = useState(null)
  const [confirmationModal, setConfirmationModal] = useState(null)

  useEffect(() => {
    ;(async () => {
      try {
        const res = await fetchCourseDetails(courseId)
        setResponse(res)
      } catch (error) {
        console.log("Could not fetch Course Details")
      }
    })()
  }, [courseId])

  // ✅ FIXED: Avg rating safe
  const [avgReviewCount, setAvgReviewCount] = useState(0)
  useEffect(() => {
    const count = GetAvgRating(
      response?.data?.courseDetails?.ratingAndReviews || []
    )
    setAvgReviewCount(isNaN(count) ? 0 : count)
  }, [response])

  const [isActive, setIsActive] = useState([])
  const handleActive = (id) => {
    setIsActive(
      !isActive.includes(id)
        ? isActive.concat([id])
        : isActive.filter((e) => e !== id)
    )
  }

  // ✅ FIXED: safe lecture count
  const [totalNoOfLectures, setTotalNoOfLectures] = useState(0)
  useEffect(() => {
    let lectures = 0
    response?.data?.courseDetails?.courseContent?.forEach((sec) => {
      lectures += sec?.subSection?.length || 0
    })
    setTotalNoOfLectures(lectures)
  }, [response])

  if (loading || !response) {
    return (
      <div className="grid min-h-[calc(100vh-3.5rem)] place-items-center">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!response.success) {
    return <Error />
  }

  const {
    courseName,
    courseDescription,
    thumbnail,
    price,
    whatYouWillLearn,
    courseContent,
    ratingAndReviews,
    instructor,
    studentsEnrolled,
    createdAt,
  } = response.data?.courseDetails

  const handleBuyCourse = () => {
    if (token) {
      buyCourse(token, [courseId], user, navigate, dispatch)
      return
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to Purchase Course.",
      btn1Text: "Login",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    })
  }

  if (paymentLoading) {
    return (
      <div className="grid min-h-[calc(100vh-3.5rem)] place-items-center">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <>
      <div className={`relative w-full bg-richblack-800`}>
        <div className="mx-auto box-content px-4 lg:w-[1260px] 2xl:relative ">
          <div className="mx-auto grid min-h-[450px] max-w-maxContentTab justify-items-center py-8 lg:mx-0 lg:justify-items-start lg:py-0 xl:max-w-[810px]">

            <div className="relative block max-h-[30rem] lg:hidden">
              <div className="absolute bottom-0 left-0 h-full w-full shadow-[#161D29_0px_-64px_36px_-28px_inset]"></div>
              <img src={thumbnail} alt="course thumbnail" className="w-full" />
            </div>

            <div className="z-30 my-5 flex flex-col gap-4 py-5 text-lg text-richblack-5">
              <p className="text-4xl font-bold">{courseName}</p>
              <p className="text-richblack-200">{courseDescription}</p>

              {/* ✅ FIXED AREA */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-yellow-25">
                  {isNaN(avgReviewCount) ? 0 : avgReviewCount}
                </span>

                <RatingStars
                  Review_Count={isNaN(avgReviewCount) ? 0 : avgReviewCount}
                  Star_Size={24}
                />

                <span>{`(${ratingAndReviews?.length || 0} reviews)`}</span>

                <span>
                  {`${studentsEnrolled?.length || 0} students enrolled`}
                </span>
              </div>

              <p>
                Created By{" "}
                {`${instructor?.firstName || ""} ${
                  instructor?.lastName || ""
                }`}
              </p>

              <div className="flex flex-wrap gap-5 text-lg">
                <p className="flex items-center gap-2">
                  <BiInfoCircle /> Created at {formatDate(createdAt)}
                </p>
                <p className="flex items-center gap-2">
                  <HiOutlineGlobeAlt /> English
                </p>
              </div>
            </div>

            <div className="flex w-full flex-col gap-4 border-y py-4 lg:hidden">
              <p className="text-3xl font-semibold">
                Rs. {price || 0}
              </p>

              <button className="yellowButton" onClick={handleBuyCourse}>
                Buy Now
              </button>
              <button className="blackButton">Add to Cart</button>
            </div>
          </div>

          <div className="hidden lg:block lg:absolute right-[1rem] top-[60px] w-1/3">
            <CourseDetailsCard
              course={response?.data?.courseDetails}
              setConfirmationModal={setConfirmationModal}
              handleBuyCourse={handleBuyCourse}
            />
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 text-richblack-5 lg:w-[1260px]">
        <div className="xl:max-w-[810px]">

          <div className="my-8 border p-8">
            <p className="text-3xl font-semibold">What you'll learn</p>
            <ReactMarkdown>
              {whatYouWillLearn || ""}
            </ReactMarkdown>
          </div>

          <div>
            <p className="text-[28px] font-semibold">Course Content</p>

            <div className="flex justify-between flex-wrap">
              <div className="flex gap-2">
                <span>{courseContent?.length || 0} section(s)</span>
                <span>{totalNoOfLectures} lecture(s)</span>
                <span>{response?.data?.totalDuration || "0h"}</span>
              </div>

              <button
                className="text-yellow-25"
                onClick={() => setIsActive([])}
              >
                Collapse all
              </button>
            </div>

            <div className="py-4">
              {courseContent?.map((course, index) => (
                <CourseAccordionBar
                  key={index}
                  course={course}
                  isActive={isActive}
                  handleActive={handleActive}
                />
              ))}
            </div>

            <div className="mb-12 py-4">
              <p className="text-[28px] font-semibold">Author</p>

              <div className="flex items-center gap-4 py-4">
                <img
                  src={
                    instructor?.image ||
                    `https://api.dicebear.com/5.x/initials/svg?seed=${instructor?.firstName} ${instructor?.lastName}`
                  }
                  alt="Author"
                  className="h-14 w-14 rounded-full"
                />

                <p>
                  {`${instructor?.firstName || ""} ${
                    instructor?.lastName || ""
                  }`}
                </p>
              </div>

              <p>{instructor?.additionalDetails?.about || ""}</p>
            </div>

          </div>
        </div>
      </div>

      <Footer />
      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </>
  )
}

export default CourseDetails